import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';

import { TokenFormat } from 'src/app/models/filename-conventions/token-format';
import { chipLabelOf, classifyChip } from '../chip-classifier';

export type TokenVocabularyItemKind = 'token' | 'custom-slot';

export interface TokenVocabularyItem {
  slug: string;
  label: string;
  kind?: TokenVocabularyItemKind;
}

const TOKEN_PATTERN = /^\{([^{}]+)\}$/;
const EXTENSION_SLUG = 'extension';
const DOT_SEPARATOR = '.';

const ALLOWED_CUSTOM_CHAR_PATTERN = /^[A-Za-z0-9_\-.]$/;
const CONTROL_KEYS: readonly string[] = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'Tab',
  'Enter',
  'Escape',
];

export const FIXED_SEPARATORS: readonly string[] = ['_', '-', '.'];

@Component({
  selector: 'app-token-chip-picker',
  templateUrl: './token-chip-picker.component.html',
  styleUrls: ['./token-chip-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class TokenChipPickerComponent implements OnChanges {
  @Input() value: readonly TokenFormat[] = [];
  @Input() vocabulary: readonly TokenVocabularyItem[] = [];
  @Input() hint: string = 'Drag to reorder tokens';
  @Input() placeholder: string = 'Add tokens or separators';
  @Input() label: string | null = null;
  @Input() showReset: boolean = false;

  @Output() readonly valueChange = new EventEmitter<TokenFormat[]>();

  @ViewChildren('customInput') customInputs?: QueryList<ElementRef<HTMLInputElement>>;

  chips: TokenFormat[] = [];
  readonly fixedSeparators = FIXED_SEPARATORS;

  get standardTokens(): readonly TokenVocabularyItem[] {
    return this.vocabulary.filter((item) => item.kind !== 'custom-slot');
  }

  get customSlotItems(): readonly TokenVocabularyItem[] {
    return this.vocabulary.filter((item) => item.kind === 'custom-slot');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.chips = this.hydrate(this.value);
    }
  }

  addToken(item: TokenVocabularyItem): void {
    const appended = this.buildAppendedChipsForToken(item);
    this.commit(appended);
  }

  addSeparator(char: string): void {
    if (!this.fixedSeparators.includes(char)) return;
    const next: TokenFormat = { order: 0, value: char, origin: 'Template' };
    this.commit(this.insertBeforeExtension(this.chips, next));
  }

  addTemplateCustomChip(): void {
    const custom: TokenFormat = { order: 0, value: '', origin: 'Template' };
    this.commit(this.insertBeforeExtension(this.chips, custom));
    queueMicrotask(() => this.focusLastCustomInput());
  }

  updateTemplateCustomChip(chipIndex: number, value: string): void {
    if (chipIndex < 0 || chipIndex >= this.chips.length) return;
    const target = this.chips[chipIndex];
    if (!this.isTemplateCustomChip(target)) return;
    const sanitised = this.sanitiseCustomValue(value);
    if (sanitised === target.value) return;
    const before = this.chips.slice(0, chipIndex);
    const after = this.chips.slice(chipIndex + 1);
    const updated: TokenFormat = { order: 0, value: sanitised, origin: 'Template' };
    this.commit([...before, updated, ...after]);
  }

  resetCustomValues(): void {
    if (!this.chips.some((chip) => this.isTemplateCustomChip(chip))) return;
    const cleared = this.chips.map((chip) =>
      this.isTemplateCustomChip(chip)
        ? { order: chip.order, value: '', origin: chip.origin }
        : chip,
    );
    this.commit(cleared);
  }

  removeAt(index: number): void {
    if (index < 0 || index >= this.chips.length) return;
    const next = this.chips.filter((_, i) => i !== index);
    this.commit(next);
  }

  drop(event: CdkDragDrop<TokenFormat[]>): void {
    const next = [...this.chips];
    moveItemInArray(next, event.previousIndex, event.currentIndex);
    this.commit(this.clampAfterExtension(next));
  }

  onCustomKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (CONTROL_KEYS.includes(event.key)) return;
    if (event.key.length !== 1) return;
    if (ALLOWED_CUSTOM_CHAR_PATTERN.test(event.key)) return;
    event.preventDefault();
  }

  isToken(chip: TokenFormat): boolean {
    return classifyChip(chip.value) === 'token';
  }

  isCustomSlot(chip: TokenFormat): boolean {
    const match = TOKEN_PATTERN.exec(chip.value);
    if (!match) return false;
    return match[1].toLowerCase() === 'custom';
  }

  isTemplateCustomChip(chip: TokenFormat): boolean {
    if (this.isToken(chip)) return false;
    if (this.isFixedSeparator(chip)) return false;
    return true;
  }

  chipLabel(chip: TokenFormat): string {
    const labelValue = chipLabelOf(chip.value);
    if (labelValue === chip.value) return chip.value;
    const found = this.vocabulary.find((v) => v.slug === labelValue);
    return found?.label ?? labelValue;
  }

  trackChip(index: number, _chip: TokenFormat): string {
    return `${index}`;
  }

  private isFixedSeparator(chip: TokenFormat): boolean {
    return chip.value.length === 1 && FIXED_SEPARATORS.includes(chip.value);
  }

  private sanitiseCustomValue(value: string): string {
    return Array.from(value)
      .filter((char) => ALLOWED_CUSTOM_CHAR_PATTERN.test(char))
      .join('');
  }

  private buildAppendedChipsForToken(item: TokenVocabularyItem): TokenFormat[] {
    const tokenChip: TokenFormat = { order: 0, value: `{${item.slug}}`, origin: 'Template' };
    if (item.slug !== EXTENSION_SLUG) {
      return this.insertBeforeExtension(this.chips, tokenChip);
    }
    if (this.chips.length === 0) {
      return [...this.chips, tokenChip];
    }
    if (this.chips[this.chips.length - 1].value === DOT_SEPARATOR) {
      return [...this.chips, tokenChip];
    }
    const dotChip: TokenFormat = { order: 0, value: DOT_SEPARATOR, origin: 'Template' };
    return [...this.chips, dotChip, tokenChip];
  }

  private insertBeforeExtension(
    chips: readonly TokenFormat[],
    incoming: TokenFormat,
  ): TokenFormat[] {
    const trailerStart = this.findExtensionTrailerStart(chips);
    if (trailerStart < 0) return [...chips, incoming];
    return [...chips.slice(0, trailerStart), incoming, ...chips.slice(trailerStart)];
  }

  private findExtensionTrailerStart(chips: readonly TokenFormat[]): number {
    const extToken = `{${EXTENSION_SLUG}}`;
    const extIndex = chips.findIndex((c) => c.value === extToken);
    if (extIndex < 0) return -1;
    if (extIndex >= 1 && chips[extIndex - 1].value === DOT_SEPARATOR) return extIndex - 1;
    return extIndex;
  }

  private clampAfterExtension(chips: readonly TokenFormat[]): TokenFormat[] {
    const extToken = `{${EXTENSION_SLUG}}`;
    if (!this.vocabulary.some((v) => v.slug === EXTENSION_SLUG)) return [...chips];
    const extIndex = chips.findIndex((c) => c.value === extToken);
    if (extIndex < 1 || chips[extIndex - 1].value !== DOT_SEPARATOR) return [...chips];
    const dotIndex = extIndex - 1;
    const alreadyPinned = extIndex === chips.length - 1 && dotIndex === chips.length - 2;
    if (alreadyPinned) return [...chips];
    const head = chips.slice(0, dotIndex);
    const tail = chips.slice(dotIndex);
    const displaced = tail.filter((c, i) => {
      if (i === 0) return false;
      if (i === 1 && c.value === extToken) return false;
      return true;
    });
    return [
      ...head,
      ...displaced,
      { order: 0, value: DOT_SEPARATOR, origin: 'Template' },
      { order: 0, value: extToken, origin: 'Template' },
    ];
  }

  private hydrate(source: readonly TokenFormat[]): TokenFormat[] {
    return [...source]
      .sort((a, b) => a.order - b.order)
      .map((chip, index) => this.stampReordered(chip, index));
  }

  private commit(chips: readonly TokenFormat[]): void {
    this.chips = this.renumber(chips);
    this.valueChange.emit(this.chips);
  }

  private renumber(chips: readonly TokenFormat[]): TokenFormat[] {
    return chips.map((chip, index) => this.stampReordered(chip, index));
  }

  private stampReordered(chip: TokenFormat, index: number): TokenFormat {
    return chip.origin === undefined
      ? { order: index, value: chip.value }
      : { order: index, value: chip.value, origin: chip.origin };
  }

  private focusLastCustomInput(): void {
    if (!this.customInputs) return;
    const list = this.customInputs.toArray();
    if (list.length === 0) return;
    const last = list[list.length - 1];
    last.nativeElement.focus();
  }
}
